import type { ShapeElementModel } from '@blocksuite/affine-model';

import type { RoughCanvas } from '../../../rough/canvas.js';
import type { Renderer } from '../../renderer.js';
import { type Colors, drawGeneralShape } from './utils.js';

/**
 * "magic number" for bezier approximations of arcs (http://itc.ktu.lt/itc354/Riskus354.pdf)
 */
const K_RECT = 1 - 0.5522847498;

export function rect(
  model: ShapeElementModel,
  ctx: CanvasRenderingContext2D,
  matrix: DOMMatrix,
  renderer: Renderer,
  rc: RoughCanvas,
  colors: Colors
) {
  const {
    filled,
    radius,
    rotate,
    roughness,
    seed,
    shapeStyle,
    strokeStyle,
    strokeWidth,
  } = model;
  const [, , w, h] = model.deserializedXYWH;
  const renderOffset = Math.max(strokeWidth, 0) / 2;
  const renderWidth = w - renderOffset * 2;
  const renderHeight = h - renderOffset * 2;
  const r =
    radius < 1 ? Math.min(renderWidth * radius, renderHeight * radius) : radius;
  const cx = renderWidth / 2;
  const cy = renderHeight / 2;

  const { fillColor, strokeColor } = colors;

  ctx.setTransform(
    matrix
      .translateSelf(renderOffset, renderOffset)
      .translateSelf(cx, cy)
      .rotateSelf(rotate)
      .translateSelf(-cx, -cy)
  );

  if (shapeStyle === 'General') {
    drawGeneralShape(ctx, model, renderer, filled, fillColor, strokeColor);
  } else {
    rc.path(
      `
      M ${r} 0
      L ${renderWidth - r} 0
      C ${renderWidth - K_RECT * r} 0 ${renderWidth} ${K_RECT * r
      } ${renderWidth} ${r}
      L ${renderWidth} ${renderHeight - r}
      C ${renderWidth} ${renderHeight - K_RECT * r} ${renderWidth - K_RECT * r
      } ${renderHeight} ${renderWidth - r} ${renderHeight}
      L ${r} ${renderHeight}
      C ${K_RECT * r} ${renderHeight} 0 ${renderHeight - K_RECT * r} 0 ${renderHeight - r
      }
      L 0 ${r}
      C 0 ${K_RECT * r} ${K_RECT * r} 0 ${r} 0
      Z
      `,
      {
        seed,
        roughness,
        strokeLineDash: strokeStyle === 'dash' ? [12, 12] : undefined,
        stroke: strokeStyle === 'none' ? 'none' : strokeColor,
        strokeWidth,
        fill: filled ? fillColor : undefined,
      }
    );
  }

  // Render image content if provided on the model (imageUrl or imageSvg)
  const anyModel = model as unknown as {
    imageUrl?: string;
    imageSvg?: string;
    __imgCache?: HTMLImageElement;
    __imgSrc?: string;
    isIcon?: boolean;
    textDisplay?: boolean;
    tags?: Array<{ name: string; color?: string }>;
  };
  const imgSrc =
    anyModel.imageUrl ??
    (anyModel.imageSvg ? `data:image/svg+xml;utf8,${encodeURIComponent(anyModel.imageSvg)}` : undefined);

  if (imgSrc) {
    if (!anyModel.__imgCache || anyModel.__imgSrc !== imgSrc) {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        // trigger a re-render once the image is ready
        renderer.refresh();
      };
      img.src = imgSrc;
      anyModel.__imgCache = img;
      anyModel.__imgSrc = imgSrc;
    }
    const img = anyModel.__imgCache;
    if (img && img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      ctx.save();
      // Clip to the same rounded-rect as the node
      const rr = r;
      ctx.beginPath();
      ctx.moveTo(rr, 0);
      ctx.lineTo(renderWidth - rr, 0);
      ctx.quadraticCurveTo(renderWidth, 0, renderWidth, rr);
      ctx.lineTo(renderWidth, renderHeight - rr);
      ctx.quadraticCurveTo(renderWidth, renderHeight, renderWidth - rr, renderHeight);
      ctx.lineTo(rr, renderHeight);
      ctx.quadraticCurveTo(0, renderHeight, 0, renderHeight - rr);
      ctx.lineTo(0, rr);
      ctx.quadraticCurveTo(0, 0, rr, 0);
      ctx.closePath();
      ctx.clip();

      // Check if this is an icon that should be displayed with text
      if (anyModel.isIcon && anyModel.textDisplay !== false) {
        // Icon mode: draw very small icon at the top, leave plenty of space for text
        const iconSize = Math.min(renderWidth * 0.15, renderHeight * 0.15, 12);
        const iconX = (renderWidth - iconSize) / 2;
        const iconY = renderHeight * 0.08;

        ctx.drawImage(img, iconX, iconY, iconSize, iconSize);
      } else {
        // Full image mode: draw the image stretched to the rect's renderable area
        ctx.drawImage(img, 0, 0, renderWidth, renderHeight);
      }
      ctx.restore();
    }
  }

  // Render tags below text if provided
  if (anyModel.tags && anyModel.tags.length > 0 && anyModel.textDisplay !== false) {
    ctx.save();
    
    // Stack tags vertically for better appearance
    const tagHeight = Math.min(12, renderHeight * 0.08);
    const iconSize = tagHeight * 0.55;
    const iconPadding = iconSize * 0.9;
    const tagPadding = tagHeight * 0.35;
    const tagVerticalSpacing = tagHeight * 0.5;
    
    // Start tags at 65% down from the top (well below center text)
    let currentY = renderHeight * 0.65;
    
    ctx.font = `${tagHeight * 0.7}px sans-serif`;
    
    // Render up to 3 tags stacked vertically
    for (const tag of anyModel.tags.slice(0, 3)) {
      // Truncate with ellipsis if needed
      let tagText = tag.name;
      const maxChars = 10;
      if (tagText.length > maxChars) {
        tagText = tagText.slice(0, maxChars - 1) + '…';
      }
      
      const textMetrics = ctx.measureText(tagText);
      const maxTagWidth = renderWidth * 0.6;
      const tagWidth = Math.min(textMetrics.width + tagPadding * 2 + iconPadding + iconSize, maxTagWidth);
      
      // Center the tag horizontally
      const tagX = (renderWidth - tagWidth) / 2;
      
      // Pill-shaped tag with faint fill using globalAlpha
      const tagRadius = tagHeight * 0.5;
      
      // Set the base color once
      const baseColor = tag.color || 'rgba(139, 92, 246, 1)';
      
      // Very faint fill
      ctx.globalAlpha = 0.2;
      ctx.fillStyle = baseColor;
      ctx.beginPath();
      ctx.roundRect(tagX, currentY, tagWidth, tagHeight, tagRadius);
      ctx.fill();
      
      // Border with full color
      ctx.globalAlpha = 1;
      ctx.strokeStyle = baseColor;
      ctx.lineWidth = 0.5;
      ctx.stroke();
      
      // Draw hashtag icon with full color
      ctx.globalAlpha = 1;
      ctx.strokeStyle = baseColor;
      ctx.lineWidth = iconSize * 0.1;
      ctx.lineCap = 'round';
      
      const iconX = tagX + tagPadding * 0.8;
      const iconY = currentY + (tagHeight - iconSize) / 2;
      
      // Draw hashtag symbol
      ctx.beginPath();
      // Vertical lines
      ctx.moveTo(iconX + iconSize * 0.35, iconY);
      ctx.lineTo(iconX + iconSize * 0.35, iconY + iconSize);
      ctx.moveTo(iconX + iconSize * 0.65, iconY);
      ctx.lineTo(iconX + iconSize * 0.65, iconY + iconSize);
      // Horizontal lines
      ctx.moveTo(iconX, iconY + iconSize * 0.35);
      ctx.lineTo(iconX + iconSize, iconY + iconSize * 0.35);
      ctx.moveTo(iconX, iconY + iconSize * 0.65);
      ctx.lineTo(iconX + iconSize, iconY + iconSize * 0.65);
      ctx.stroke();
      
      // Tag text - use the same color as the main element text
      ctx.globalAlpha = 1;
      ctx.fillStyle = colors.color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(tagText, tagX + tagWidth / 2 + iconSize * 0.25, currentY + tagHeight / 2);
      
      currentY += tagHeight + tagVerticalSpacing;
    }
    
    // Reset globalAlpha
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  ctx.setTransform(
    ctx
      .getTransform()
      .translateSelf(cx, cy)
      .rotateSelf(-rotate)
      .translateSelf(-cx, -cy)
      .translateSelf(-renderOffset, -renderOffset)
      .translateSelf(cx, cy)
      .rotateSelf(rotate)
      .translateSelf(-cx, -cy)
  );
}
